/**
 * The two ways in, in Harvey's look (day 33; PRD section 2, "Sign up and sign
 * in", Mohit, 17 October 2026):
 *
 *   Sign in                 the account's email, then the code sent to it, or
 *                           Google, or a passkey once Clerk offers them
 *   Create your account     the email, the code, then the profile and consent
 *                           (ProfileStep), and the account opens at once:
 *                           nobody approves it (the API has let every account
 *                           straight in since 28 September 2026)
 *
 * Sign in is every signed out address; Create your account is
 * /create-account, so it can be linked to. Each page says what to do if the
 * address belongs on the other: an address with no account, typed into Sign
 * in, is offered Create your account with it, in one click; one that already
 * has an account, typed into Create your account, is simply signed in, and
 * told so. Underneath, Clerk is asked first whether the address has an
 * account, then the matching flow runs (ADR-010).
 *
 * Clerk sends and checks the code. What the portal adds is the wording, the
 * layout, and the one device rule, which the API applies on the first request
 * after this screen finishes.
 *
 * Also the screen a student lands on after being signed out, saying why.
 */

import { useRef, useState } from "preact/hooks";
import { HELP_EMAIL, WORDMARK } from "../brand";
import { PASSKEY_SIGN_IN } from "../config";
import { SIGNED_OUT_WORDING, type SignedOutReason } from "../shell/session";
import { Button } from "../ui";
import { clerkErrorCode, clerkErrorMessage, loadClerk, type ClerkLike, type SignInLike, type SignUpLike } from "./clerk";

/** The product's name, as new screens print it (brand.ts, WORDMARK). */
const NAME = `${WORDMARK.lead}${WORDMARK.tail}`;

/** Create your account's own address. Every other signed out address is Sign in. */
export const CREATE_ACCOUNT_PATH = "/create-account";

export type SignedOutPage = "sign_in" | "create";

type Step =
  | { readonly kind: "email" }
  | { readonly kind: "code"; readonly email: string; readonly flow: "sign_in" | "sign_up" };

export interface SignInProps {
  /**
   * Clerk, once it has loaded. Null while it is still starting: the form is on
   * screen before Clerk is ready (a second or more, on a phone), and a student
   * who submits in that time simply waits for it, since every action below asks
   * for Clerk when it runs rather than when the form was drawn.
   */
  readonly clerk: ClerkLike | null;
  readonly reason: SignedOutReason | null;
  /** Which page: Sign in unless the address is /create-account. */
  readonly page?: SignedOutPage;
  /** Go to the other page, keeping what was typed. */
  readonly onPage?: (page: SignedOutPage) => void;
  /**
   * Clerk has a session now. The caller asks the API who it belongs to. If it
   * returns a promise the form stays busy until it settles, so the screen is
   * not offered again in the moment between signing in and the next step.
   */
  readonly onSignedIn: () => void | Promise<void>;
  /**
   * The admin website's sign in (apps/admin): an existing Clerk user's emailed
   * code, and nothing else. No account is ever created there and there is no
   * Google button, so the only way in is an address that already exists.
   */
  readonly adminOnly?: boolean;
  /** Offer a passkey. config.ts's switch; a test may set it. */
  readonly passkeys?: boolean;
}

/** Where Google sends the student back to. App.tsx finishes the sign in there. */
export const SSO_CALLBACK_PATH = "/sso-callback";

export function SignIn(props: SignInProps) {
  const page: SignedOutPage = props.adminOnly ? "sign_in" : (props.page ?? "sign_in");
  const passkeys = !props.adminOnly && (props.passkeys ?? PASSKEY_SIGN_IN);
  const [step, setStep] = useState<Step>({ kind: "email" });
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  /** Sign in was given an address with no account: offer to create one with it. */
  const [noAccount, setNoAccount] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The resource each flow is on, as Clerk last returned it.
  const signIn = useRef<SignInLike | null>(null);
  const signUp = useRef<SignUpLike | null>(null);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setSent(null);
    setNoAccount(null);
    try {
      await action();
    } catch (caught) {
      setError(clerkErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const ready = async (): Promise<ClerkLike> => props.clerk ?? (await loadClerk());

  const client = async () => {
    const clerk = await ready();
    if (!clerk.client) throw new Error("The sign in service has not started. Reload the page.");
    return clerk.client;
  };

  /** A sign up for an address with no account. Clerk's bot check, if the instance has one, renders into #clerk-captcha. */
  const startSignUp = async (address: string) => {
    const created = await (await client()).signUp.create({ emailAddress: address });
    signUp.current = await created.prepareEmailAddressVerification({ strategy: "email_code" });
    setStep({ kind: "code", email: address, flow: "sign_up" });
  };

  /** Sends a code: to the account if the address has one, otherwise as this page says. */
  const sendCode = (address: string, as: SignedOutPage) =>
    run(async () => {
      try {
        const started = await (await client()).signIn.create({ identifier: address });
        const factor = started.supportedFirstFactors?.find((f) => f.strategy === "email_code");
        if (!factor?.emailAddressId) {
          throw new Error("This account cannot sign in with an emailed code. Try Continue with Google.");
        }
        signIn.current = await started.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId: factor.emailAddressId,
        });
        setStep({ kind: "code", email: address, flow: "sign_in" });
        if (as === "create") setSent(`${address} already has an account, so this signs you in.`);
      } catch (caught) {
        if (clerkErrorCode(caught) !== "form_identifier_not_found") throw caught;
        if (props.adminOnly) throw new Error("That address cannot sign in here.");
        if (as === "sign_in") {
          setNoAccount(address);
          return;
        }
        await startSignUp(address);
      }
    });

  /** Sign in's offer, taken: Create your account, with the address already typed, and its code sent. */
  const createWith = (address: string) => {
    props.onPage?.("create");
    void run(() => startSignUp(address));
  };

  const resend = () =>
    run(async () => {
      if (step.kind !== "code") return;
      if (step.flow === "sign_up" && signUp.current) {
        signUp.current = await signUp.current.prepareEmailAddressVerification({ strategy: "email_code" });
        setSent(`A new code is on its way to ${step.email}.`);
        return;
      }
      // A sign in's factor id lives on the resource, so start it again.
      const address = step.email;
      const started = await (await client()).signIn.create({ identifier: address });
      const factor = started.supportedFirstFactors?.find((f) => f.strategy === "email_code");
      if (!factor?.emailAddressId) throw new Error("A new code could not be sent. Start again.");
      signIn.current = await started.prepareFirstFactor({ strategy: "email_code", emailAddressId: factor.emailAddressId });
      setSent(`A new code is on its way to ${address}.`);
    });

  const finish = async (sessionId: string | null) => {
    await (await ready()).setActive({ session: sessionId });
    await props.onSignedIn();
  };

  const verify = (code: string) =>
    run(async () => {
      if (step.kind !== "code") return;
      if (step.flow === "sign_in") {
        const current = signIn.current;
        if (!current) throw new Error("Start again: send a new code.");
        const result = await current.attemptFirstFactor({ strategy: "email_code", code });
        signIn.current = result;
        if (result.status !== "complete") {
          throw new Error(`This account needs another step to sign in, which ${NAME} does not support yet. Write to ${HELP_EMAIL}.`);
        }
        await finish(result.createdSessionId);
        return;
      }
      const current = signUp.current;
      if (!current) throw new Error("Start again: send a new code.");
      const result = await current.attemptEmailAddressVerification({ code });
      signUp.current = result;
      if (result.status !== "complete") {
        throw new Error(`The account could not be finished (${result.missingFields.join(", ") || result.status}). Write to ${HELP_EMAIL}.`);
      }
      await finish(result.createdSessionId);
    });

  const google = () =>
    run(async () => {
      const origin = window.location.origin;
      await (await client()).signIn.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: `${origin}${SSO_CALLBACK_PATH}`,
        redirectUrlComplete: `${origin}/`,
      });
    });

  const passkey = () =>
    run(async () => {
      const resource = (await client()).signIn;
      if (!resource.authenticateWithPasskey) throw new Error("Passkeys are not available here yet. Use your email.");
      const result = await resource.authenticateWithPasskey({ flow: "discoverable" });
      if (result.status !== "complete") throw new Error("That passkey did not sign you in. Use your email.");
      await finish(result.createdSessionId);
    });

  const alert = error ? (
    <p class="notice bad auth-alert" role="alert">
      {error}
    </p>
  ) : sent ? (
    <p class="notice auth-alert" role="status">
      {sent}
    </p>
  ) : null;

  if (step.kind === "code") {
    return (
      <div class="auth-step">
        {step.flow === "sign_up" ? <p class="eyebrow">Step 1 of 2</p> : null}
        <h1>Check your email</h1>
        <p class="auth-lede">
          Enter the code sent to <b>{step.email}</b>.
        </p>
        {alert}
        <form
          class="auth-form-body"
          onSubmit={(event) => {
            event.preventDefault();
            const code = [...(event.currentTarget as HTMLFormElement).querySelectorAll<HTMLInputElement>("[data-otp]")]
              .map((input) => input.value)
              .join("");
            if (code.length === 6) void verify(code);
            else setError("Enter all six digits.");
          }}
        >
          <CodeBoxes disabled={busy} onComplete={(code) => void verify(code)} />
          <Button variant="primary" type="submit" block disabled={busy}>
            Verify
          </Button>
          <div class="auth-row">
            <Button
              variant="quiet"
              size="sm"
              onClick={() => {
                setError(null);
                setSent(null);
                setStep({ kind: "email" });
              }}
            >
              Change address
            </Button>
            <Button variant="quiet" size="sm" disabled={busy} onClick={() => void resend()}>
              Resend code
            </Button>
          </div>
        </form>
      </div>
    );
  }

  const wording = props.reason ? SIGNED_OUT_WORDING[props.reason] : null;
  const creating = page === "create";
  return (
    <div class="auth-step">
      {wording ? (
        <>
          <p class="eyebrow">Signed out</p>
          <h1>{wording.title}</h1>
          <p class="auth-lede">{wording.body}</p>
        </>
      ) : props.adminOnly ? (
        <h1>Admin sign in</h1>
      ) : creating ? (
        <>
          <p class="eyebrow">Step 1 of 2</p>
          <h1>Create your account</h1>
        </>
      ) : (
        <>
          <h1>Sign in to {NAME}</h1>
        </>
      )}
      {alert}
      {noAccount ? (
        <div class="notice auth-alert auth-offer" role="status">
          <span>
            No account uses <b>{noAccount}</b> yet.
          </span>
          <Button size="sm" disabled={busy} onClick={() => createWith(noAccount)}>
            Create your account
          </Button>
        </div>
      ) : null}
      <form
        class="auth-form-body"
        onSubmit={(event) => {
          event.preventDefault();
          const address = email.trim();
          if (address) void sendCode(address, page);
        }}
      >
        <div class="field">
          <label for="email">{creating ? "Email" : "Account email"}</label>
          <input
            class="input"
            id="email"
            type="email"
            autocomplete={creating ? "email" : "username"}
            inputMode="email"
            required
            value={email}
            onInput={(event) => {
              setEmail((event.currentTarget as HTMLInputElement).value);
              setNoAccount(null);
            }}
          />
        </div>
        <div id="clerk-captcha" class="auth-captcha" />
        <Button variant="primary" type="submit" block disabled={busy}>
          {busy ? "Sending..." : "Email me a code"}
        </Button>
      </form>
      {props.adminOnly ? null : (
        <>
          <div class="auth-or" role="separator">
            <span>or</span>
          </div>
          <div class="auth-ways">
            <button type="button" class="btn block auth-google" disabled={busy} onClick={() => void google()}>
              <GoogleMark />
              Continue with Google
            </button>
            {passkeys && !creating ? (
              <Button block disabled={busy} onClick={() => void passkey()}>
                Sign in with a passkey
              </Button>
            ) : null}
          </div>
          <p class="auth-switch">
            {creating ? (
              <>
                Already have an account?{" "}
                <a href="/" onClick={(event) => switchTo(event, "sign_in", props.onPage)}>
                  Sign in
                </a>
              </>
            ) : (
              <>
                New to {NAME}?{" "}
                <a href={CREATE_ACCOUNT_PATH} onClick={(event) => switchTo(event, "create", props.onPage)}>
                  Create your account
                </a>
              </>
            )}
          </p>
          {creating ? <p class="auth-terms">By continuing you accept the Terms of Use and Privacy Policy.</p> : null}
        </>
      )}
    </div>
  );
}

/** The other page, without a reload, when the caller can switch; otherwise the link is followed. */
function switchTo(event: MouseEvent, page: SignedOutPage, onPage: ((page: SignedOutPage) => void) | undefined): void {
  if (!onPage) return;
  event.preventDefault();
  onPage(page);
}

/** Google's mark, as its sign in branding guidelines ask buttons to show it. */
function GoogleMark() {
  return (
    <svg class="auth-google-mark" viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <path class="g-blue" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path class="g-green" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path class="g-yellow" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z" />
      <path class="g-red" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.97 8.97 0 0 0 9 0 9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

/**
 * Six boxes: typing moves on, backspace moves back, and a pasted code fills
 * them all. The last digit submits. The sign in code and the firm code both
 * use them.
 */
export function CodeBoxes(props: { disabled: boolean; label?: string; onComplete: (code: string) => void }) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  const fill = (from: number, digits: string) => {
    let index = from;
    for (const digit of digits) {
      const box = boxes.current[index];
      if (!box) break;
      box.value = digit;
      index += 1;
    }
    const next = boxes.current[Math.min(index, 5)];
    next?.focus();
    const code = boxes.current.map((box) => box?.value ?? "").join("");
    if (code.length === 6) props.onComplete(code);
  };

  return (
    <div class="otp" role="group" aria-label={props.label ?? "One time code"}>
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
          key={index}
          ref={(element) => {
            boxes.current[index] = element;
          }}
          data-otp={index}
          inputMode="numeric"
          autocomplete={index === 0 ? "one-time-code" : "off"}
          maxLength={index === 0 ? 6 : 1}
          aria-label={`Digit ${index + 1}`}
          disabled={props.disabled}
          autoFocus={index === 0}
          onInput={(event) => {
            const input = event.currentTarget as HTMLInputElement;
            const digits = input.value.replace(/\D/g, "");
            input.value = "";
            if (digits) fill(index, digits);
          }}
          onPaste={(event) => {
            const digits = (event.clipboardData?.getData("text") ?? "").replace(/\D/g, "").slice(0, 6);
            if (!digits) return;
            event.preventDefault();
            fill(index, digits);
          }}
          onKeyDown={(event) => {
            const input = event.currentTarget as HTMLInputElement;
            if (event.key === "Backspace" && !input.value && index > 0) boxes.current[index - 1]?.focus();
          }}
        />
      ))}
    </div>
  );
}
